import { useEffect } from "react";
import { useDomainState, useUi } from "../../../state/editor/useEditor";
import { Icon } from "../../kit";
import { useStudioModelId } from "../studioModel";
import { useConfiguratorDraft } from "./draft";

/**
 * Basic › Share's "Customer configurator" switch: shows or hides the choices
 * bar on the canvas (the same flag as the top bar's "Configurator" button),
 * says in a line what a shopper will get, and offers "Preview as customer".
 */
export function ConfiguratorSwitch({ pad = "px-[18px] pt-4" }: { pad?: string }) {
  const [state, draft] = useConfiguratorDraft();
  const modelId = useStudioModelId();
  const models = useDomainState("models");
  const [, patchUi] = useUi();
  useEffect(() => draft.bindModel(modelId), [draft, modelId]);

  const withChoices = state.def.groups.filter((g) => g.options.length > 0);
  const choices = withChoices.reduce((n, g) => n + g.options.length, 0);
  const status =
    choices === 0
      ? "Pick a part on the bar under the picture, then press + to add the materials customers can choose."
      : `${choices} choice${choices === 1 ? "" : "s"} on ${withChoices.length} part${withChoices.length === 1 ? "" : "s"}.`;
  const saveHint = !modelId
    ? " Put this piece in your Vault to share them."
    : draft.dirty
      ? " Press Save on the bar to put them on your link."
      : "";

  return (
    <section className={pad} data-config-switch>
      <div className="flex items-start gap-3 rounded-[10px] border border-ed-border px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <span className="block text-[13px] font-medium leading-[17px] text-ed-text">Customer configurator</span>
          <span className="block text-[12px] leading-4 text-ed-text-2">
            Shoppers on your link switch the metal and stones themselves.
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={state.on}
          aria-label="Customer configurator"
          onClick={() => draft.setOn(!state.on)}
          className={`relative mt-0.5 h-6 w-10 shrink-0 rounded-full transition-colors ${state.on ? "bg-ed-gold" : "bg-ed-line/40"}`}
          data-config-toggle
        >
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${state.on ? "left-[18px]" : "left-0.5"}`} />
        </button>
      </div>
      {state.on ? (
        <p className="mt-2 text-[12px] leading-[17px] text-ed-text-2" data-config-status>
          {state.load === "loading" ? "Loading this piece's choices…" : status + saveHint}
        </p>
      ) : null}
      {models.length > 0 ? (
        <button
          type="button"
          onClick={() => patchUi({ customerPreview: true })}
          className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold leading-4 text-ed-accent hover:underline"
          data-config-switch-preview
        >
          <Icon name="visibility" size={16} />
          Preview as customer
        </button>
      ) : null}
    </section>
  );
}
